import React from "react";
import { Link } from "react-router-dom";
import { UserReport } from "../types/user-report.entity";
import { displayDate } from "src/utils/dateHandler.util";
import { useSetUsersReportHandledStateMutation } from "../services/user-report.service";

type UserReportWithId = UserReport & { id: string };
type Props = {
  data: UserReportWithId[];
};

export const UsersReportsTable: React.FC<Props> = ({ data }) => {
  const [setUsersReportHandledState, { isLoading }] = useSetUsersReportHandledStateMutation();
  const [pendingReportId, setPendingReportId] = React.useState<string | null>(null);

  const linkClass = (variant: "reporter" | "reported") =>
    variant === "reporter"
      ? "flex items-center gap-2 p-2 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200 hover:shadow-md hover:from-amber-100 hover:to-orange-100 hover:border-amber-300 transition-all duration-200 cursor-pointer group no-underline"
      : "flex items-center gap-2 p-2 bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg border border-rose-200 hover:shadow-md hover:from-rose-100 hover:to-pink-100 hover:border-rose-300 transition-all duration-200 cursor-pointer group no-underline";

  const badgeClass = (variant: "reporter" | "reported") =>
    variant === "reporter"
      ? "flex items-center justify-center px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wide"
      : "flex items-center justify-center px-2 py-1 bg-gradient-to-r from-rose-500 to-pink-500 rounded text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wide";

  const idClass = (variant: "reporter" | "reported") =>
    variant === "reporter"
      ? "text-[9px] sm:text-[10px] font-mono font-bold text-amber-700 group-hover:text-amber-900 transition-colors duration-200 break-all"
      : "text-[9px] sm:text-[10px] font-mono font-bold text-rose-700 group-hover:text-rose-900 transition-colors duration-200 break-all";

  const handleToggleHandled = async (report: UserReportWithId) => {
    setPendingReportId(report.id);
    try {
      await setUsersReportHandledState({
        id: report.id,
        isHandled: !report.isHandled,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update report handled state:", error);
    } finally {
      setPendingReportId(null);
    }
  };

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[900px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gray-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Reporter / Reported</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Date</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">From / Reason</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider w-2/5 min-w-[320px]">Explanation</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center py-4 text-xl text-gray-500">
                No reports found
              </td>
            </tr>
          )}
          {data.map((r) => {
            const createdAt = r.createdAt instanceof Date ? r.createdAt : (r.createdAt as any)?.toDate?.() ?? undefined;
            const isPending = isLoading && pendingReportId === r.id;

            return (
              <tr
                key={r.id}
                className={`text-center transition-colors ${
                  r.isHandled
                    ? "bg-emerald-50/70 even:bg-emerald-50/70 hover:bg-emerald-100/80"
                    : "odd:bg-white even:bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <div className="flex flex-col gap-2 min-w-[200px]">
                    <Link
                      to={`/users/${r.reporterId}`}
                      className={linkClass("reporter")}
                      title={`View reporter: ${r.reporterId}`}
                    >
                      <div className={badgeClass("reporter")}>Reporter</div>
                      <div className="flex-1 min-w-0">
                        <div className={idClass("reporter")}>{r.reporterId}</div>
                      </div>
                    </Link>
                    <Link
                      to={`/users/${r.reportedId}`}
                      className={linkClass("reported")}
                      title={`View reported user: ${r.reportedId}`}
                    >
                      <div className={badgeClass("reported")}>Reported</div>
                      <div className="flex-1 min-w-0">
                        <div className={idClass("reported")}>{r.reportedId}</div>
                      </div>
                    </Link>
                  </div>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">
                      {createdAt ? createdAt.toLocaleDateString() : displayDate(r.createdAt)}
                    </span>
                    <span className="text-[9px] sm:text-[10px] text-gray-500">
                      {createdAt ? createdAt.toLocaleTimeString() : ""}
                    </span>
                  </div>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200 text-left">
                  <div className="inline-flex min-w-[130px] flex-col items-start gap-1.5 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-3 py-2 text-left shadow-sm">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700">
                        From
                      </span>
                      <span className="text-[10px] sm:text-[11px] font-semibold text-slate-700">
                        {r.reportedFrom}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-700">
                        Reason
                      </span>
                      <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] sm:text-xs font-medium text-slate-700">
                        {r.reason}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200 text-left w-2/5 min-w-[320px] max-w-[600px]">
                  <p className="text-[10px] sm:text-xs text-gray-700 whitespace-pre-wrap break-words" title={r.explanation}>
                    {r.explanation}
                  </p>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-3">
                    <span
                      className={`min-w-[64px] text-left text-[10px] sm:text-xs font-semibold ${
                        r.isHandled ? "text-emerald-700" : "text-amber-700"
                      }`}
                    >
                      {isPending ? "Updating..." : r.isHandled ? "Handled" : "Pending"}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(r.isHandled)}
                      aria-label={`Toggle report ${r.id} status`}
                      onClick={() => handleToggleHandled(r)}
                      disabled={isPending}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                        r.isHandled
                          ? "border-emerald-400 bg-emerald-500 focus:ring-emerald-300"
                          : "border-amber-300 bg-amber-400 focus:ring-amber-300"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={r.isHandled ? "Switch to pending" : "Switch to handled"}
                    >
                      <span className="sr-only">
                        {r.isHandled ? "Mark as pending" : "Mark as handled"}
                      </span>
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                          r.isHandled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default UsersReportsTable;
